import React, { memo, useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import type { ScreenCartProps } from '@/entities/campaign';

const ScreenCart: React.FC<ScreenCartProps> = memo(
  ({
    cartOpen,
    setCartOpen,
    formik,
    handleToggleScreen,
    handleRemoveScreen,
  }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [cartSearch, setCartSearch] = useState<string>('');

    // Get all target groups from formik
    const targetGroups = formik.values.targetGroups || [];

    // Get campaign dates
    const campaignStartDate = formik.values.campaignStartDate;
    const campaignEndDate = formik.values.campaignEndDate;

    // Calculate number of days in campaign
    const numberOfDays = useMemo(() => {
      if (campaignStartDate && campaignEndDate) {
        const start = dayjs(campaignStartDate);
        const end = dayjs(campaignEndDate);

        if (start.isValid() && end.isValid()) {
          const timeDiff = end.diff(start, 'day');
          const days = Math.ceil(timeDiff) + 1; // +1 to include both start and end dates
          return Math.max(1, days); // Ensure at least 1 day
        }
      }
      return 0;
    }, [campaignStartDate, campaignEndDate]);

    // Calculate combined data from all target groups
    const { totalScreens, totalPrice, screensByTouchpoint } = useMemo(() => {
      const allScreens: any[] = [];
      const byTouchpoint: Record<string, any[]> = {};
      let totalPriceSum = 0;

      targetGroups.forEach((targetGroup: any) => {
        if (targetGroup?.selectedArea?.length > 0) {
          allScreens.push(...targetGroup.selectedArea);

          // Group screens by touchpoint
          targetGroup.selectedArea.forEach((screen: any) => {
            const touchpointName =
              screen.touchpoint?.name || 'Unknown Touchpoint';

            if (!byTouchpoint[touchpointName]) {
              byTouchpoint[touchpointName] = [];
            }
            byTouchpoint[touchpointName].push(screen);
          });

          // Calculate price based on campaign duration: (price / 30) * numberOfDays
          totalPriceSum += targetGroup.selectedArea.reduce(
            (sum: number, screen: any) => {
              const screenPrice = Number(screen.price) || 0;
              const numberOfBookedSlots =
                screen.numberOfBookedSlots || screen.consumedSlots || 1;

              if (numberOfDays > 0) {
                const dailyPrice = screenPrice / 30;
                const screenCost =
                  dailyPrice * numberOfDays * numberOfBookedSlots;
                return sum + screenCost;
              }
              // If no campaign dates, show monthly price
              return sum + screenPrice * numberOfBookedSlots;
            },
            0
          );
        }
      });

      return {
        allSelectedScreens: allScreens,
        totalScreens: allScreens.length,
        totalPrice: totalPriceSum,
        screensByTouchpoint: byTouchpoint,
      };
    }, [targetGroups, numberOfDays]);

    // Filter screens per touchpoint by search query
    const filteredScreensByTouchpoint = useMemo(() => {
      const query = cartSearch.toLowerCase();
      const result: Record<string, any[]> = {};
      Object.entries(screensByTouchpoint).forEach(
        ([name, screens]: [string, any[]]) => {
          const filtered = query
            ? screens.filter((s: any) =>
                (s.name || '').toLowerCase().includes(query)
              )
            : screens;
          result[name] = filtered;
        }
      );
      return result;
    }, [screensByTouchpoint, cartSearch]);

    // Helper function to calculate screen price based on campaign duration
    const calculateScreenPrice = (screen: any) => {
      const screenPrice = Number(screen.price) || 0;
      const numberOfBookedSlots =
        screen.numberOfBookedSlots || screen.consumedSlots || 1;

      if (numberOfDays > 0) {
        const dailyPrice = screenPrice / 30;
        return dailyPrice * numberOfDays * numberOfBookedSlots;
      }
      // If no campaign dates, show monthly price
      return screenPrice * numberOfBookedSlots;
    };

    // Handle screen removal from cart
    const handleDeleteScreen = (screen: any) => {
      if (handleRemoveScreen) {
        handleRemoveScreen(screen);
      } else {
        // Fallback to toggle if handleRemoveScreen is not provided
        handleToggleScreen(screen);
      }
    };

    return (
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            width: 400,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          mt={6}
        >
          <Typography variant="h6">Selected Screens (All Locations)</Typography>
          <IconButton onClick={() => setCartOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {totalScreens > 0 ? (
          <>
            <TextField
              size="small"
              fullWidth
              placeholder="Search screens..."
              value={cartSearch}
              onChange={e => setCartSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': { height: 36 },
              }}
            />
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {Object.entries(screensByTouchpoint).map(
                ([touchpointName, screens]: [string, any[]]) => (
                  <Accordion
                    key={touchpointName}
                    defaultExpanded={true}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '8px !important',
                      '&.Mui-expanded': {
                        margin: '0 0 8px 0',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: isDarkMode
                          ? theme.palette.background.default
                          : theme.palette.grey[100],
                        borderRadius: '8px',
                        '&.Mui-expanded': {
                          borderRadius: '8px 8px 0 0',
                        },
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          margin: '8px 0',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color="primary"
                          sx={{ flexGrow: 1 }}
                        >
                          {touchpointName}
                        </Typography>
                        <Chip
                          label={`${screens.length} screen${screens.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                      <List sx={{ width: '100%' }}>
                        {filteredScreensByTouchpoint[touchpointName]?.length ===
                        0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 3, textAlign: 'center' }}
                          >
                            No screens found for "{cartSearch}"
                          </Typography>
                        ) : (
                          filteredScreensByTouchpoint[touchpointName]?.map(
                            (screen: any) => (
                              <ListItem
                                key={`${touchpointName}-${screen.id}`}
                                divider
                                sx={{
                                  px: 2,
                                  '&:last-child': {
                                    borderBottom: 'none',
                                  },
                                }}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleDeleteScreen(screen)}
                                    size="small"
                                    sx={{
                                      color: 'error.main',
                                      '&:hover': {
                                        backgroundColor: 'error.light',
                                        color: 'white',
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  primary={screen.name}
                                  secondary={
                                    <>
                                      {`Slots: ${screen.consumedSlots || screen.numberOfBookedSlots || 1}`}
                                      {' · '}
                                      {`Price: ₹${calculateScreenPrice(screen).toLocaleString('en-IN', { maximumFractionDigits: 2 })}${numberOfDays > 0 ? ` (${numberOfDays} days)` : '/month'}`}
                                    </>
                                  }
                                  primaryTypographyProps={{
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '0.8rem',
                                    color: 'text.secondary',
                                  }}
                                />
                              </ListItem>
                            )
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )
              )}
            </Box>

            {/* Footer summary */}
            <Box
              display="flex"
              flexDirection="column"
              mt={2}
              pt={2}
              borderTop={`1px solid ${theme.palette.divider}`}
              gap={1}
              sx={{
                backgroundColor: isDarkMode
                  ? theme.palette.background.default
                  : theme.palette.grey[50],
                borderRadius: '8px',
                p: 2,
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body1" fontWeight={600}>
                  Total Screens: {totalScreens}
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  Total Price: ₹
                  {totalPrice.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Across {Object.keys(screensByTouchpoint).length} touchpoint
                {Object.keys(screensByTouchpoint).length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            sx={{ color: 'text.secondary' }}
          >
            <Typography variant="body2" textAlign="center">
              No screens selected yet.
            </Typography>
            <Typography variant="caption" textAlign="center" sx={{ mt: 1 }}>
              Select screens from different locations to see them here.
            </Typography>
          </Box>
        )}
      </Drawer>
    );
  }
);

ScreenCart.displayName = 'ScreenCart';

export default ScreenCart;
